import React, { useState, useEffect } from "react";
import { fetchTags, createTag, updateTag, deleteTag } from "../../services/api";
import { Tag } from "../../services/api";
import { HiOutlinePencil, HiOutlineTrash, HiPlus } from "react-icons/hi";
import "./Admin.css";

const TagsManagement: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [tagName, setTagName] = useState<string>("");
  const [tagDescription, setTagDescription] = useState<string>("");

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const fetchedTags = await fetchTags();
      setTags(fetchedTags);
      setLoading(false);
    } catch (err: any) {
      setError(`Failed to load tags: ${err.message}`);
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditingTag(null);
    setTagName("");
    setTagDescription("");
    setShowModal(true);
  };

  const handleEditClick = (tag: Tag) => {
    setEditingTag(tag);
    setTagName(tag.name);
    setTagDescription(tag.description);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!tagName.trim()) {
      setError("Tag name is required");
      return;
    }

    try {
      if (editingTag) {
        // Update existing tag
        await updateTag(editingTag.id, {
          name: tagName.trim(),
          description: tagDescription.trim(),
        });
      } else {
        // Create new tag
        await createTag({
          name: tagName.trim(),
          description: tagDescription.trim(),
        });
      }

      setShowModal(false);
      loadTags();
    } catch (err: any) {
      setError(
        `Failed to ${editingTag ? "update" : "create"} tag: ${err.message}`
      );
    }
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Are you sure you want to delete this tag? This action cannot be undone if the tag is in use."
      )
    ) {
      try {
        await deleteTag(id);
        loadTags();
      } catch (err: any) {
        setError(`Failed to delete tag: ${err.message}`);
      }
    }
  };

  if (loading && tags.length === 0) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#ffcc33]"></div>
          <p className="mt-2 text-[#140d0c]">Loading tags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#140d0c]">Tags Management</h2>
        <button
          onClick={handleCreateClick}
          className="bg-[#ffcc33] text-[#140d0c] px-4 py-2 rounded-md hover:bg-[#ffcc33]/90 flex items-center"
        >
          <HiPlus className="mr-2" />
          Create Tag
        </button>
      </div>

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
          <button
            className="ml-2 text-red-700 hover:text-red-900"
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        {tags.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No tags found. Create your first tag to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Tag Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tags.map((tag) => (
                  <tr key={tag.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-sm rounded-full bg-[#f2efe3] text-[#6c6c6c]">
                        {tag.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {tag.description || (
                          <span className="text-gray-400 italic">
                            No description
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleEditClick(tag)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit"
                        >
                          <HiOutlinePencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(tag.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <HiOutlineTrash className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Tag Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-[#140d0c] mb-4">
              {editingTag ? "Edit Tag" : "Create New Tag"}
            </h3>

            <div className="mb-4">
              <label
                htmlFor="tagName"
                className="block text-sm font-medium text-[#140d0c] mb-1"
              >
                Tag Name
              </label>
              <input
                type="text"
                id="tagName"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ffcc33] focus:border-transparent"
                placeholder="Enter tag name"
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="tagDescription"
                className="block text-sm font-medium text-[#140d0c] mb-1"
              >
                Description
              </label>
              <textarea
                id="tagDescription"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ffcc33] focus:border-transparent"
                rows={3}
                placeholder="Enter tag description (optional)"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#ffcc33] text-[#140d0c] rounded-md hover:bg-[#ffcc33]/90"
              >
                {editingTag ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsManagement;
